import { useCallback, useEffect, useRef, useState } from 'react'
import { isEqual } from 'lodash-es'

import { DefaultOptionType } from 'antd/es/select'
import FormControlLabel from '@mui/material/FormControlLabel'
import Tooltip from '@mui/material/Tooltip'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'

import { ISelectOption } from '$/types'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useGetStatesQuery } from 'app/services/api/states'
import { useGetRegionsQuery } from 'app/services/api/regions'
import { useGetOperatorsQuery } from 'app/services/api/operators'
import {
  setEditInProgressSync,
  setFilteredNetworkListSync,
  setStagingProfileSync,
  setStagingProfileValidationErrorsSync
} from 'app/layout/Navbar/Filters/globalFilterSlice'
import {
  selectStagingProfile,
  selectStagingProfileValidationErrors
} from 'app/layout/Navbar/Filters/globalFilterSelectors'
import { IFilterModalChildComponentProps } from 'app/layout/Navbar/Filters/FilterModal/types'
import { useLazyGetContinuousRoutesFilterProfileQuery } from 'app/services/api/filterprofiles'
import {
  LOCAL_PROFILE_ID,
  UNSAVED_PROFILE_NAME
} from 'app/layout/Navbar/Filters/FilterModal/constants'

import { alphanumericRegex } from 'constants/regex'
import { mapOSRToSelectOption } from 'common/utils/filter/systemScope'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'
import StyledToggle from 'common/components/StyledToggle'

import { selectBaseSettings } from 'features/Settings/settingsSelectors'

interface ISystemScopeProfileDetail extends IFilterModalChildComponentProps {
  latestProfileSelectionTimestamp: number
  initiallyPrivate: boolean
  setInitiallyPrivate: React.Dispatch<React.SetStateAction<boolean>>
}

const ProfileDetails = ({
  latestProfileSelectionTimestamp,
  setSearchValue,
  initiallyPrivate,
  setInitiallyPrivate,
  setOnlyShowSelected
}: ISystemScopeProfileDetail) => {
  const dispatch = useAppDispatch()
  const profileNameRef = useRef<HTMLInputElement>(null)

  const baseSettings = useAppSelector(selectBaseSettings)
  const stagingProfile = useAppSelector(selectStagingProfile)
  const validationErrors = useAppSelector(selectStagingProfileValidationErrors)

  const [selectedOperators, setSelectedOperators] = useState<ISelectOption[]>(
    []
  )

  const [selectedStates, setSelectedStates] = useState<ISelectOption[]>([])
  const [selectedRegions, setSelectedRegions] = useState<ISelectOption[]>([])
  const [nameValidationMessage, setNameValidationMessage] = useState<string>('')

  // Unfiltered OSR option lists
  // REVIEW: Eliminate the need to specify an empty object when there's no filters
  const { data: operatorList } = useGetOperatorsQuery({})
  const { data: stateList } = useGetStatesQuery({})
  const { data: regionList } = useGetRegionsQuery({})

  const [getProfileContinuousRoutes] =
    useLazyGetContinuousRoutesFilterProfileQuery()

  /**
   * OSR option lists that are filtered based on other two filters
   * Example: If operatorCode 'BC' (British Columbia) and regionCode 'Region1' (Region 1)
   * is selected, then stateOptions will be filtered based on those two filters
   */
  const { currentData: stateOptions } = useGetStatesQuery({
    operatorCodes: selectedOperators.map((o) => o.value.toString()),
    regionCodes: selectedRegions.map((r) => r.value.toString())
  })

  const { currentData: regionOptions } = useGetRegionsQuery({
    operatorCodes: selectedOperators.map((o) => o.value.toString()),
    stateCodes: selectedStates.map((s) => s.value.toString())
  })

  const { currentData: operatorOptions } = useGetOperatorsQuery({
    regionCodes: selectedRegions.map((r) => r.value.toString()),
    stateCodes: selectedStates.map((s) => s.value.toString())
  })

  const [lastProfileId, setLastProfileId] = useState<string>(LOCAL_PROFILE_ID)

  useEffect(() => {
    if (
      stagingProfile?.isPrivate !== undefined &&
      lastProfileId !== stagingProfile?.id
    ) {
      setInitiallyPrivate(stagingProfile.isPrivate)
    }

    setLastProfileId(stagingProfile?.id ?? LOCAL_PROFILE_ID)
  }, [lastProfileId, setInitiallyPrivate, stagingProfile])

  // REVIEW: Consider saving string as CONST? but they are only used here
  const globalFilterSettings = baseSettings.find(
    (e) => e.groupName === 'GlobalFilterSettings'
  )

  const dropdownOrder = globalFilterSettings?.details.find(
    (e) => e.key === 'FilterDropdownOrder'
  )

  /**
   * This should be the only place we set component state
   * as we treat `stagingProfile` as the source of truth.
   *
   * If we set it component state elsewhere, it will become difficult to
   * keep it in sync in scenario when we switch to a different
   * `stagingProfile`.
   */
  useEffect(() => {
    if (
      stagingProfile == null &&
      (selectedOperators.length > 0 ||
        selectedStates.length > 0 ||
        selectedRegions.length > 0)
    ) {
      setSelectedStates([])
      setSelectedOperators([])
      setSelectedRegions([])

      return
    }

    if (stagingProfile == null) {
      return
    }

    if (
      !isEqual(
        selectedOperators.map((o) => o.value),
        stagingProfile.operators.map((o) => o.code)
      )
    ) {
      setSelectedOperators(
        stagingProfile.operators.map((o) => ({
          label: o.name,
          value: o.code
        }))
      )
    }

    if (
      !isEqual(
        selectedStates.map((s) => s.value),
        stagingProfile.states.map((s) => s.code)
      )
    ) {
      setSelectedStates(
        stagingProfile.states.map((s) => ({
          label: s.name,
          value: s.code
        }))
      )
    }

    if (
      !isEqual(
        selectedRegions.map((r) => r.value),
        stagingProfile.regions.map((r) => r.code)
      )
    ) {
      setSelectedRegions(
        stagingProfile.regions.map((r) => ({
          label: r.name,
          value: r.code
        }))
      )
    }
  }, [selectedOperators, selectedStates, selectedRegions, stagingProfile])

  // Profile Name error message
  useEffect(() => {
    const { nameTaken, nameRegex } = validationErrors

    if (nameTaken ?? false) {
      setNameValidationMessage('Profile name is already in use')
    } else if (nameRegex ?? false) {
      setNameValidationMessage(
        'Profile name can only contain alphanumeric characters and underscore'
      )
    } else {
      setNameValidationMessage('')
    }
  }, [validationErrors])

  const handleToggleChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ): void => {
    if (stagingProfile != null) {
      dispatch(setEditInProgressSync(true))

      dispatch(
        setStagingProfileSync({
          ...stagingProfile,
          isPrivate: checked
        })
      )
    }
  }

  const handleProfileNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (stagingProfile != null) {
      validateProfileName(event.target.value)
      dispatch(setEditInProgressSync(true))

      dispatch(
        setStagingProfileSync({
          ...stagingProfile,
          name: event.target.value
        })
      )
    }
  }

  const validateProfileName = (profileName: string): void => {
    const profileNameDOMElement = profileNameRef.current

    if (profileNameDOMElement == null) {
      return
    }

    dispatch(
      setStagingProfileValidationErrorsSync({
        ...validationErrors,
        nameRegex: !profileNameDOMElement.checkValidity(),
        // NOTE: UNSAVED_PROFILE is a frontend only concept..
        nameTaken:
          profileName.toLowerCase() === UNSAVED_PROFILE_NAME.toLowerCase()
      })
    )
  }

  const handleOperatorSelection = (
    selectedOperators: DefaultOptionType[] | undefined
  ): void => {
    if (stagingProfile == null || operatorList == null) {
      return
    }

    // Get operators selected
    const selectedOperatorIds = (selectedOperators ?? []).map((o) =>
      o.value?.toString()
    )

    const selectedOperatorList = operatorList.filter((o) =>
      selectedOperatorIds.includes(o.code.toString())
    )

    // Logic that ensures only operators belonging to the selected states
    // are selected is offloaded to one of the useEffect. So that logic
    // can be applied reactively
    if (!isEqual(stagingProfile.operators, selectedOperatorList)) {
      dispatch(setEditInProgressSync(true))

      dispatch(
        setStagingProfileSync({
          ...stagingProfile,
          operators: selectedOperatorList
        })
      )
    }
  }

  const handleStateSelection = (
    selectedStates: DefaultOptionType[] | undefined
  ): void => {
    if (stagingProfile == null || stateList == null) {
      return
    }

    // Get and save states selected
    const selectedStateIds = (selectedStates ?? []).map((s) =>
      s.value?.toString()
    )

    const selectedStateList = stateList.filter((s) =>
      selectedStateIds.includes(s.code)
    )

    if (!isEqual(stagingProfile.states, selectedStateList)) {
      dispatch(setEditInProgressSync(true))

      dispatch(
        setStagingProfileSync({
          ...stagingProfile,
          states: selectedStateList
        })
      )
    }
  }

  const handleRegionSelection = (
    selectedRegions: DefaultOptionType[] | undefined
  ): void => {
    if (stagingProfile == null || regionList == null) {
      return
    }

    // Get and save states selected
    const selectedRegionIds = (selectedRegions ?? []).map((r) =>
      r.value?.toString()
    )

    const selectedRegionList = regionList.filter((r) =>
      selectedRegionIds.includes(r.code.toString())
    )

    if (!isEqual(stagingProfile.regions, selectedRegionList)) {
      dispatch(setEditInProgressSync(true))

      dispatch(
        setStagingProfileSync({
          ...stagingProfile,
          regions: selectedRegionList
        })
      )
    }
  }

  const handleSearch = useCallback(
    (resetSelection: boolean) => {
      const body = {
        operatorCodes: [] as string[],
        stateCodes: [] as string[],
        regionCodes: [] as string[],
        customQuery: '',
        customQueryString: ''
      }

      if (stagingProfile != null) {
        if (setOnlyShowSelected != null) {
          setOnlyShowSelected(false)
        }

        if (setSearchValue != null) {
          setSearchValue('')
        }

        if (resetSelection) {
          dispatch(setEditInProgressSync(true))

          dispatch(
            setStagingProfileSync({
              ...stagingProfile,
              continuousRoutes: []
            })
          )
        }

        const { operators, states, regions } = stagingProfile

        body.operatorCodes = operators.map((o) => o.code)
        body.stateCodes = states.map((s) => s.code)
        body.regionCodes = regions.map((r) => r.code)
      }

      getProfileContinuousRoutes(body)
        .unwrap()
        .then((res) => dispatch(setFilteredNetworkListSync(res)))
    },
    [
      dispatch,
      getProfileContinuousRoutes,
      setOnlyShowSelected,
      setSearchValue,
      stagingProfile
    ]
  )

  useEffect(() => {
    // Refresh network/line list on profile selection change
    handleSearch(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestProfileSelectionTimestamp])

  /**
   * Get conditions to determine when a dropdown should be disabled
   * @param prerequisites - array of variables to listen for
   *
   * NOTE: Currently handles max of 2 elements in prerequisites array
   */
  const getDisableCondition = (prerequisites?: ISelectOption[][]): boolean => {
    if (prerequisites != null) {
      if (prerequisites.length === 2) {
        return (
          stagingProfile == null ||
          prerequisites[0].length <= 0 ||
          prerequisites[1].length <= 0
        )
      } else if (prerequisites.length === 1) {
        return stagingProfile == null || prerequisites[0].length <= 0
      }
    }

    return stagingProfile == null
  }

  const renderRegionDropdown = (
    prerequisites?: ISelectOption[][]
  ): JSX.Element => {
    return (
      <div className='m-3'>
        <span className='mb-2 block font-semibold'>Regions</span>
        <SelectDropdownWrapper
          className='w-full'
          options={mapOSRToSelectOption(regionOptions)}
          value={selectedRegions}
          onChangeSelections={handleRegionSelection}
          disabled={getDisableCondition(prerequisites)}
          mode='multiple'
          placeholder='Select regions'
          maxTagTextLength={10}
          maxTagCount={2}
        />
      </div>
    )
  }

  const renderOperatorDropdown = (
    prerequisites?: ISelectOption[][]
  ): JSX.Element => {
    return (
      <div className='m-3'>
        <span className='mb-2 block font-semibold'>Operators</span>
        <SelectDropdownWrapper
          className='w-full'
          options={mapOSRToSelectOption(operatorOptions)}
          value={selectedOperators}
          onChangeSelections={handleOperatorSelection}
          disabled={getDisableCondition(prerequisites)}
          mode='multiple'
          placeholder='Select operators'
          maxTagTextLength={10}
          maxTagCount={2}
        />
      </div>
    )
  }

  const renderStateDropdown = (
    prerequisites?: ISelectOption[][]
  ): JSX.Element => {
    return (
      <div className='m-3'>
        <span className='mb-2 block font-semibold'>
          States/Provinces
        </span>
        <SelectDropdownWrapper
          className='w-full'
          options={mapOSRToSelectOption(stateOptions)}
          value={selectedStates}
          onChangeSelections={handleStateSelection}
          disabled={getDisableCondition(prerequisites)}
          mode='multiple'
          placeholder='Select states'
          maxTagTextLength={10}
          maxTagCount={2}
        />
      </div>
    )
  }

  /**
   * Renders operator, state, region dropdowns in order
   * based on business rules retrieved from baseSettings API
   * @param order - dropdown selection order
   */
  const renderDropdown = (order: string): JSX.Element => {
    switch (order) {
      case 'StateRegionOperator':
        return (
          <div className='flex flex-col'>
            {renderStateDropdown()}
            {renderRegionDropdown()}
            {renderOperatorDropdown()}
          </div>
        )
      case 'StateOperatorRegion':
        return (
          <div className='flex flex-col'>
            {renderStateDropdown()}
            {renderOperatorDropdown()}
            {renderRegionDropdown()}
          </div>
        )
      case 'RegionStateOperator':
        return (
          <div className='flex flex-col'>
            {renderRegionDropdown()}
            {renderStateDropdown()}
            {renderOperatorDropdown()}
          </div>
        )
      case 'RegionOperatorState':
        return (
          <div className='flex flex-col'>
            {renderRegionDropdown()}
            {renderOperatorDropdown()}
            {renderStateDropdown()}
          </div>
        )
      case 'OperatorRegionState':
        return (
          <div className='flex flex-col'>
            {renderOperatorDropdown()}
            {renderRegionDropdown()}
            {renderStateDropdown()}
          </div>
        )

      // Default = case 'OperatorStateRegion'
      default:
        return (
          <div className='flex flex-col'>
            {renderOperatorDropdown()}
            {renderStateDropdown()}
            {renderRegionDropdown()}
          </div>
        )
    }
  }

  return (
    <OverlayScrollbar>
      <div className='flex flex-1 flex-col p-7'>
        <div className='flex'>
          <div className='flex flex-col p-3'>
            <label
              htmlFor='system-scope-details-modal-profile-name'
              className='mb-2 font-semibold'
            >
              Profile Name
            </label>
            <div>
              <input
                id='system-scope-details-modal-profile-name'
                className='h-10 rounded-sm border w-full'
                type='text'
                ref={profileNameRef}
                value={stagingProfile?.name ?? 'Please select profile'}
                disabled={stagingProfile == null}
                onChange={handleProfileNameChange}
                pattern={alphanumericRegex}
                maxLength={20}
                required
              />
            </div>
            <span
              className={`w-[21.4rem] h-6 text-red text-xs leading-3 ${nameValidationMessage ? 'visible' : 'invisible'}`}
            >
              {nameValidationMessage}
            </span>
            {initiallyPrivate && (
              <div>
                <FormControlLabel
                  label='Private'
                  sx={{ mr: '8px' }}
                  control={
                    <StyledToggle
                      checked={stagingProfile?.isPrivate ?? true}
                      disabled={
                        stagingProfile == null ||
                        (stagingProfile.name === UNSAVED_PROFILE_NAME &&
                          stagingProfile.isPrivate)
                      }
                      onChange={handleToggleChange}
                    />
                  }
                />
                <Tooltip
                  title={
                    <h1 className='max-w-[9.375rem] text-sm text-white'>
                      Making a profile public is irreversible and will restrict
                      editing capabilities.
                    </h1>
                  }
                  placement='right'
                >
                  <div className='inline-block text-lighthouseBlue'>
                    <HelpOutlineRoundedIcon />
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        {renderDropdown((dropdownOrder?.value as string) ?? '')}
        <div className='flex justify-end'>
          <button
            className={[
              'm-3 h-10 px-4 py-1',
              'bg-lighthouseBlue',
              'rounded-sm font-medium text-white'
            ].join(' ')}
            onClick={() => handleSearch(true)}
          >
            SEARCH
          </button>
        </div>
      </div>
    </OverlayScrollbar>
  )
}

export default ProfileDetails
